import { FC } from 'react';
import { Box, Card, CardContent, Chip, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../../../atomic/atoms';
import { MAX_SHIPMENT_COUNT } from '../autoReturnAndResend.helpers';
import { ARAR } from '../autoReturnAndResend.classes';

interface AutoReturnAndResendSearchAreaProps {
    shippingInput: string;
    shipmentCount: number;
    isOverLimit: boolean;
    onInputChange: (value: string) => void;
    onClear: () => void;
    onSearch: () => void;
}

export const AutoReturnAndResendSearchArea: FC<AutoReturnAndResendSearchAreaProps> = ({
    shippingInput,
    shipmentCount,
    isOverLimit,
    onInputChange,
    onClear,
    onSearch,
}) => {
    const { t } = useTranslation();
    const isSearchDisabled = shipmentCount === 0 || isOverLimit;

    return (
        <Card className={ARAR.searchSection} variant="outlined" sx={{ width: '100%' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <TextField
                    className={ARAR.searchInput}
                    label={t('autoReturnAndResend.search.label')}
                    placeholder={t('autoReturnAndResend.search.placeholder')}
                    multiline
                    rows={6}
                    fullWidth
                    value={shippingInput}
                    error={isOverLimit}
                    onChange={(event) => onInputChange(event.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{
                        mb: 1,
                        '& textarea': {
                            resize: 'vertical',
                        },
                    }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Typography variant="caption" color={isOverLimit ? 'error' : 'text.secondary'}>
                            {t('autoReturnAndResend.search.shipmentCountHint', {
                                count: shipmentCount,
                                max: MAX_SHIPMENT_COUNT,
                            })}
                        </Typography>
                        {isOverLimit && (
                            <Chip size="small" color="error" label={t('autoReturnAndResend.search.limitExceeded')} />
                        )}
                    </Box>
                    <Box className={ARAR.searchActions} sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        <SCUIButton
                            text={t('general.clear')}
                            type="ghost"
                            sizeType="default"
                            size="m"
                            onClick={onClear}
                        />
                        <SCUIButton
                            text={t('general.search')}
                            type="primary"
                            sizeType="default"
                            size="m"
                            disabled={isSearchDisabled}
                            onClick={onSearch}
                        />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};
